import type { Conversation, Message, Paginator } from '@twilio/conversations';

/** OCG path prefix for general conversation endpoints (e.g. direct live chat). */
export const OCG_CONVERSATION_PREFIX = '/gateway/api/jsm/csm/api/v1/omni/conversation';

/** OCG path prefix for help-seeker-scoped help-center endpoints (e.g. chat configuration). */
export const OCG_HELPCENTER_PREFIX = '/gateway/api/jsm/csm/api/v1/helpseeker/helpcenter';

/** OCG path prefix for Twilio-specific endpoints (access-token, agent, etc.). */
export const OCG_TWILIO_CONVERSATION_PREFIX =
	'/gateway/api/jsm/csm/api/v1/omni/twilio/conversation';

/**
 * Low-level URL builder: combines a base path, a path suffix and an optional gateway origin
 * into a full OCG request URL.
 *
 * Prefer the service-scoped helpers {@link buildTwilioUrl} and {@link buildConversationUrl}
 * instead of calling this directly.
 *
 * @param basePath      – The service-specific path prefix (e.g. one of the `OCG_*_PREFIX` constants).
 * @param pathSuffix    – The endpoint path (e.g. `/access-token`).
 * @param gatewayOrigin – The gateway origin (scheme + host, e.g. `https://example.atlassian.net`).
 *                        When not provided or empty, returns basePath + pathSuffix as a relative URL (same-origin).
 *                        If a full URL with a path is provided, only the origin is used.
 * @throws {Error} If the gatewayOrigin is invalid.
 */
const buildOcgUrl = (basePath: string, pathSuffix: string, gatewayOrigin?: string): string => {
	const path = pathSuffix.startsWith('/') ? pathSuffix : `/${pathSuffix}`;
	const fullPath = `${basePath}${path}`;
	if (!gatewayOrigin) {
		return fullPath;
	}
	try {
		const { origin } = new URL(gatewayOrigin);
		return `${origin}${fullPath}`;
	} catch {
		throw new Error(`Invalid gatewayOrigin: ${gatewayOrigin}`);
	}
};

/**
 * Build a URL for Twilio-specific OCG endpoints (access-token, agent, etc.).
 *
 * @param pathSuffix    – The endpoint path (e.g. `/access-token`).
 * @param gatewayOrigin – Optional gateway origin; omit for same-origin relative URLs.
 */
export const buildTwilioUrl = (pathSuffix: string, gatewayOrigin?: string): string =>
	buildOcgUrl(OCG_TWILIO_CONVERSATION_PREFIX, pathSuffix, gatewayOrigin);

/**
 * Build a URL for general conversation OCG endpoints (e.g. direct live chat).
 *
 * @param pathSuffix    – The endpoint path (e.g. `/livechat/direct`).
 * @param gatewayOrigin – Optional gateway origin; omit for same-origin relative URLs.
 */
export const buildConversationUrl = (pathSuffix: string, gatewayOrigin?: string): string =>
	buildOcgUrl(OCG_CONVERSATION_PREFIX, pathSuffix, gatewayOrigin);

/**
 * Build a URL for help-seeker-scoped help-center OCG endpoints (e.g. chat configuration).
 *
 * @param pathSuffix    – The endpoint path (e.g. `/{helpCenterId}/chat-configuration`).
 * @param gatewayOrigin – Optional gateway origin; omit for same-origin relative URLs.
 */
export const buildHelpcenterUrl = (pathSuffix: string, gatewayOrigin?: string): string =>
	buildOcgUrl(OCG_HELPCENTER_PREFIX, pathSuffix, gatewayOrigin);

/**
 * Twilio fires tokenAboutToExpire this many seconds before the JWT expires.
 * If a stored token has less remaining lifetime than this, the SDK would fire
 * a refresh immediately after creation — so we fetch a fresh token instead.
 */
export const TOKEN_ABOUT_TO_EXPIRE_BUFFER_SECONDS = 180;

/**
 * Fetches all messages from a Twilio Conversation using the Paginator API.
 * Messages are returned in chronological order (oldest first).
 *
 * Only uses `import type` from `@twilio/conversations` — these are erased at
 * compile time and add zero bytes to the runtime bundle.
 *
 * @param conversation – The Twilio Conversation to fetch messages from.
 * @param pageSize – Number of messages to fetch per page (default 100, max supported by Twilio).
 * @param maxPages – Maximum number of pages to fetch (default 50). Safety cap to prevent unbounded memory growth.
 * @throws If fetching or paginating messages fails.
 */
export const fetchLiveChatMessageHistory = async (
	conversation: Conversation,
	pageSize = 100,
	maxPages = 50,
): Promise<Message[]> => {
	const allMessages: Message[] = [];

	let paginator: Paginator<Message> = await conversation.getMessages(pageSize);
	allMessages.push(...paginator.items);

	let pagesFetched = 1;
	while (paginator.hasPrevPage && pagesFetched < maxPages) {
		paginator = await paginator.prevPage();
		allMessages.push(...paginator.items);
		pagesFetched++;
	}

	// Sort chronologically (oldest first) since pagination goes backwards
	allMessages.sort((a, b) => {
		const dateA = a.dateCreated?.getTime() ?? 0;
		const dateB = b.dateCreated?.getTime() ?? 0;
		return dateA - dateB;
	});

	return allMessages;
};

/**
 * Decode the payload section of a JWT token (base64url → JSON).
 *
 * @throws {Error} If the JWT is malformed or the payload cannot be decoded.
 */
const decodeJwtPayload = (jwt: string): Record<string, unknown> => {
	const payload = jwt.split('.')[1];
	if (!payload) {
		throw new Error('Invalid JWT: missing payload segment');
	}
	return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
};

type JwtMetadata = {
	/** Unix seconds when token expires; 0 if not present in the payload. */
	expiration: number;
	/** Identity string from the JWT (e.g. help seeker ID); empty string if not found. */
	identity: string;
};

/**
 * Decode expiration and identity from a Twilio Conversations JWT in a single pass.
 *
 * Identity is resolved from (in order of precedence):
 * `payload.identity` → `payload.grants.identity` → `payload.sub`
 *
 * @throws {Error} If the JWT is malformed or the payload cannot be decoded.
 */
export const getJwtMetadata = (jwt: string): JwtMetadata => {
	const decoded = decodeJwtPayload(jwt);

	const expiration = typeof decoded.exp === 'number' ? decoded.exp : 0;

	const grants = decoded.grants as Record<string, unknown> | undefined;
	const rawIdentity = decoded.identity ?? grants?.identity ?? decoded.sub;
	const identity = typeof rawIdentity === 'string' ? rawIdentity : '';

	return { expiration, identity };
};
